import React, { useCallback, useRef, useState } from 'react';

import { Button } from 'components/ui/Button';

import { Fonts } from 'styles/theme';

type Category = 'profile' | 'retro' | 'values';

interface UploadImageProps {
    category: Category;
    onSelectImage: (file: File) => void;
    disabled?: boolean;
}

export function UploadImage({
    category,
    onSelectImage,
    disabled = false,
}: UploadImageProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const openPicker = useCallback(() => inputRef.current?.click(), []);
    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (f) onSelectImage(f);
            e.target.value = '';
        },
        [onSelectImage]
    );

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragging(true);
    }, []);

    const onDragLeave = useCallback(() => setIsDragging(false), []);
    const onDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setIsDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f && f.type.startsWith('image/')) onSelectImage(f);
        },
        [onSelectImage]
    );

    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={onInputChange}
                style={{
                    display: 'none',
                }}
                aria-hidden
            />

            <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && openPicker()
                }
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                aria-label={`Upload ${category} image`}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #999',
                    borderRadius: 12,
                    padding: 12,
                    textAlign: 'center',
                    opacity: disabled ? 0.6 : 1,
                    outline: isDragging ? '2px solid #555' : 'none',
                    userSelect: 'none',
                    maxWidth: 260,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 8,
                    }}
                >
                    <Fonts.XSmall>{`Drag and drop a profile image here or choose an image from your device`}</Fonts.XSmall>
                </div>

                <Button
                    type="button"
                    onClick={openPicker}
                    disabled={disabled}
                    $isSmall
                >
                    {`Choose Image`}
                </Button>
            </div>
        </div>
    );
}
